import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import Avatar from "@/components/Avatar";
import { User, Truck, Save, Loader2, Cake, Upload, Link2, X, Sparkles, BellRing, Maximize2, Minimize2 } from "lucide-react";
import { usePushNotifications } from "@/lib/usePushNotifications";
import { useDensity } from "@/lib/useDensity";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const isWelcome = new URLSearchParams(location.search).get("welcome") === "1";
  const [welcomeOpen, setWelcomeOpen] = useState(isWelcome);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const push = usePushNotifications();
  const { density, setDensity } = useDensity();
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        birth_month: user.birth_month || "",
        birth_day: user.birth_day || "",
        birth_year: user.birth_year || "",
        show_birth_year: !!user.show_birth_year,
        show_birthday_publicly: !!user.show_birthday_publicly,
        show_contact_publicly: !!user.show_contact_publicly,
        shipping_name: user.shipping_name || user.name || "",
        shipping_street: user.shipping_street || "",
        shipping_street2: user.shipping_street2 || "",
        shipping_city: user.shipping_city || "",
        shipping_state: user.shipping_state || "",
        shipping_postal: user.shipping_postal || "",
        shipping_country: user.shipping_country || "United States",
      });
    }
  }, [user]);

  if (!form) {
    return (
      <AppShell>
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin" /></div>
      </AppShell>
    );
  }

  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const body = { ...form };
      if (body.birth_month === "") body.birth_month = null;
      if (body.birth_day === "") body.birth_day = null;
      if (body.birth_year === "") body.birth_year = null;
      Object.keys(body).forEach((k) => { if (body[k] === null) delete body[k]; });
      // numeric
      if (body.birth_month) body.birth_month = parseInt(body.birth_month, 10);
      if (body.birth_day) body.birth_day = parseInt(body.birth_day, 10);
      if (body.birth_year) body.birth_year = parseInt(body.birth_year, 10);
      await api.patch("/auth/profile", body);
      await refresh();
      setOk("Profile saved.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const f = (k, v) => setForm({ ...form, [k]: v });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true); setErr(""); setOk("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setOk("Photo updated.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const saveUrl = async () => {
    if (!photoUrl.trim()) return;
    setPhotoBusy(true); setErr(""); setOk("");
    try {
      await api.post("/profile/avatar-url", { picture: photoUrl.trim() });
      await refresh();
      setPhotoUrl("");
      setOk("Photo set from URL.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true); setErr(""); setOk("");
    try {
      await api.delete("/profile/avatar");
      await refresh();
      setOk("Photo removed.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-6 max-w-3xl">
        {welcomeOpen && (
          <div
            className="bd-surface p-5 flex items-start gap-3"
            style={{ borderLeft: "4px solid var(--primary)" }}
            data-testid="profile-welcome-banner"
          >
            <Sparkles size={20} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-lg" style={{ letterSpacing: "-0.02em" }}>
                Welcome to Born Day — finish your profile.
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>
                Add a photo and your birthday so friends recognize you in groups. Takes a minute.
              </div>
            </div>
            <button
              onClick={() => setWelcomeOpen(false)}
              className="bd-btn-ghost"
              style={{ padding: "0.3rem", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-dim)" }}
              aria-label="Dismiss welcome"
              data-testid="profile-welcome-dismiss"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>Your profile</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
            Your details and where to ship gifts.
          </h1>
        </div>

        {err && <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="profile-error">{err}</div>}
        {ok && <div className="bd-chip" data-testid="profile-ok">{ok}</div>}

        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <User size={18} /> Profile photo
          </div>
          <div className="flex items-start gap-5 flex-wrap">
            <Avatar
              name={user?.name || user?.email}
              picture={user?.picture}
              size={96}
              testId="profile-avatar-preview"
            />
            <div className="flex-1 min-w-[240px] flex flex-col gap-3">
              <div className="flex gap-2 flex-wrap">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  data-testid="profile-photo-file"
                />
                <button
                  type="button"
                  className="bd-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={photoBusy}
                  data-testid="profile-photo-upload"
                >
                  {photoBusy ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} /> Upload</>}
                </button>
                {user?.picture && (
                  <button
                    type="button"
                    className="bd-btn bd-btn-ghost"
                    onClick={removePhoto}
                    disabled={photoBusy}
                    data-testid="profile-photo-remove"
                  >
                    <X size={14} /> Remove
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Link2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--text-dim)" }} />
                  <input
                    className="bd-input"
                    style={{ paddingLeft: "2rem" }}
                    placeholder="…or paste a photo URL (https://…)"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    data-testid="profile-photo-url"
                  />
                </div>
                <button
                  type="button"
                  className="bd-btn"
                  onClick={saveUrl}
                  disabled={photoBusy || !photoUrl.trim()}
                  data-testid="profile-photo-url-save"
                >
                  Save
                </button>
              </div>
              <p className="text-xs" style={{ color: "var(--text-dim)" }}>
                JPEG / PNG / WebP, up to 8MB. Auto-cropped to a 512×512 square.
              </p>
            </div>
          </div>
        </div>

        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <User size={18} /> About you
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="bd-label">Name</label>
              <input className="bd-input" value={form.name} onChange={(e) => f("name", e.target.value)} data-testid="profile-name" />
            </div>
            <div>
              <label className="bd-label">Phone</label>
              <input className="bd-input bd-mono" value={form.phone} onChange={(e) => f("phone", e.target.value)} data-testid="profile-phone" />
            </div>
          </div>
        </div>

        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-4 font-bold text-lg">
            <Cake size={18} /> Your birthday
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="bd-label">Month</label>
              <select className="bd-input" value={form.birth_month} onChange={(e) => f("birth_month", e.target.value)} data-testid="profile-birth-month">
                <option value="">—</option>
                {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
              </select>
            </div>
            <div>
              <label className="bd-label">Day</label>
              <input className="bd-input bd-mono" type="number" min="1" max="31" value={form.birth_day} onChange={(e) => f("birth_day", e.target.value)} data-testid="profile-birth-day" />
            </div>
            <div>
              <label className="bd-label">Year (optional)</label>
              <input className="bd-input bd-mono" type="number" min="1900" max={new Date().getFullYear()} value={form.birth_year} onChange={(e) => f("birth_year", e.target.value)} data-testid="profile-birth-year" />
            </div>
          </div>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.show_birth_year} onChange={(e) => f("show_birth_year", e.target.checked)} data-testid="profile-show-year" />
            Share my birth year with the group (off by default)
          </label>
        </div>

        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-2 font-bold text-lg">
            Group visibility
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
            Choose what shows up next to your name when people open the contact list of a group you&apos;re in.
          </p>
          <div className="space-y-3">
            <label
              className="flex items-start gap-3 p-3 rounded cursor-pointer"
              style={{ border: "1px solid var(--border)" }}
              data-testid="toggle-show-birthday"
            >
              <input
                type="checkbox"
                checked={form.show_birthday_publicly}
                onChange={(e) => f("show_birthday_publicly", e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Show my birthday to group members</div>
                <div className="text-xs" style={{ color: "var(--text-dim)" }}>
                  Month + day. Year stays hidden unless you also turn on &quot;Share my birth year&quot;.
                </div>
              </div>
            </label>
            <label
              className="flex items-start gap-3 p-3 rounded cursor-pointer"
              style={{ border: "1px solid var(--border)" }}
              data-testid="toggle-show-contact"
            >
              <input
                type="checkbox"
                checked={form.show_contact_publicly}
                onChange={(e) => f("show_contact_publicly", e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">Show my email and phone to group members</div>
                <div className="text-xs" style={{ color: "var(--text-dim)" }}>
                  Lets people reach you outside Born Day. Off by default.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* iOS install walkthrough — only renders when on iOS Safari
            AND Born Day isn't yet installed as a PWA. Shown ABOVE the
            Phone-alerts toggle so the user sees the install steps
            before they try to flip the switch (which won't work in
            mobile Safari without the install). */}
        <IOSInstallPrompt compact={false} testIdPrefix="ios-install-profile" />

        {/* Personal UI density — Steve (Feb 2026): let each user shrink
            buttons / cards / spacing for their own device. Two presets
            instead of sliders to keep this simple. */}
        <div className="bd-surface p-5" data-testid="density-section">
          <div className="flex items-center gap-2 mb-2 font-bold text-lg">
            <Maximize2 size={18} /> Display density
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
            Choose how tight you want buttons, cards and spacing on your screen.
            Just a personal preference — it doesn&apos;t affect anyone else.
          </p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="UI density">
            <button
              type="button"
              onClick={() => setDensity("comfortable")}
              className="flex flex-col items-start gap-1 p-3 rounded text-left"
              style={
                density === "comfortable"
                  ? { background: "color-mix(in srgb, var(--primary) 12%, transparent)", border: "1px solid var(--primary)" }
                  : { background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }
              }
              data-testid="density-comfortable"
              aria-pressed={density === "comfortable"}
            >
              <span className="flex items-center gap-2 font-semibold text-sm">
                <Maximize2 size={14} /> Comfortable
              </span>
              <span className="text-xs" style={{ color: "var(--text-dim)" }}>
                Roomy buttons, generous spacing. Best for big monitors.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setDensity("compact")}
              className="flex flex-col items-start gap-1 p-3 rounded text-left"
              style={
                density === "compact"
                  ? { background: "color-mix(in srgb, var(--primary) 12%, transparent)", border: "1px solid var(--primary)" }
                  : { background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }
              }
              data-testid="density-compact"
              aria-pressed={density === "compact"}
            >
              <span className="flex items-center gap-2 font-semibold text-sm">
                <Minimize2 size={14} /> Compact
              </span>
              <span className="text-xs" style={{ color: "var(--text-dim)" }}>
                Tighter, more on-screen at once. Good for laptops + tablets.
              </span>
            </button>
          </div>
        </div>

        {push.supported && (
          <div className="bd-surface p-5" data-testid="push-section">
            <div className="flex items-center gap-2 mb-2 font-bold text-lg">
              <BellRing size={18} /> Phone alerts
            </div>
            <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
              Get a native phone notification when family goes Live — even when Born Day is closed.
              Uses the same OS channel as Gmail or WhatsApp, so no extra battery or data drain.
            </p>
            <label
              className="flex items-start gap-3 p-3 rounded cursor-pointer"
              style={{ border: "1px solid var(--border)" }}
              data-testid="toggle-push"
            >
              <input
                type="checkbox"
                checked={push.enabled}
                disabled={push.loading}
                onChange={(e) => (e.target.checked ? push.subscribe() : push.unsubscribe())}
                className="mt-1"
                data-testid="toggle-push-input"
              />
              <div className="flex-1">
                <div className="font-semibold text-sm flex items-center gap-2">
                  🔔 Get phone alerts when family goes live
                  {push.loading && <Loader2 size={12} className="animate-spin" />}
                </div>
                <div className="text-xs" style={{ color: "var(--text-dim)" }}>
                  On iPhone: works after you tap <em>Share → Add to Home Screen</em> and open Born Day from the home-screen icon.
                </div>
                {push.error && (
                  <div className="text-xs mt-1" style={{ color: "#ff3b30" }} data-testid="push-error">
                    {push.error}
                  </div>
                )}
              </div>
            </label>
          </div>
        )}

        <div className="bd-surface p-5">
          <div className="flex items-center gap-2 mb-2 font-bold text-lg">
            <Truck size={18} /> Shipping address
          </div>
          <p className="text-xs mb-4" style={{ color: "var(--text-dim)" }}>
            Used as the default ship-to for baby registry deliveries. Each baby event can also use a one-off override.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="bd-label">Full name on the package</label>
              <input className="bd-input" value={form.shipping_name} onChange={(e) => f("shipping_name", e.target.value)} data-testid="profile-shipping-name" />
            </div>
            <div className="sm:col-span-2">
              <label className="bd-label">Street address</label>
              <input className="bd-input" value={form.shipping_street} onChange={(e) => f("shipping_street", e.target.value)} data-testid="profile-shipping-street" />
            </div>
            <div className="sm:col-span-2">
              <label className="bd-label">Apt / Suite / Unit (optional)</label>
              <input className="bd-input" value={form.shipping_street2} onChange={(e) => f("shipping_street2", e.target.value)} data-testid="profile-shipping-street2" />
            </div>
            <div>
              <label className="bd-label">City</label>
              <input className="bd-input" value={form.shipping_city} onChange={(e) => f("shipping_city", e.target.value)} data-testid="profile-shipping-city" />
            </div>
            <div>
              <label className="bd-label">State / Region</label>
              <input className="bd-input" value={form.shipping_state} onChange={(e) => f("shipping_state", e.target.value)} data-testid="profile-shipping-state" />
            </div>
            <div>
              <label className="bd-label">Postal code</label>
              <input className="bd-input bd-mono" value={form.shipping_postal} onChange={(e) => f("shipping_postal", e.target.value)} data-testid="profile-shipping-postal" />
            </div>
            <div>
              <label className="bd-label">Country</label>
              <input className="bd-input" value={form.shipping_country} onChange={(e) => f("shipping_country", e.target.value)} data-testid="profile-shipping-country" />
            </div>
          </div>
        </div>

        <button className="bd-btn self-start" onClick={save} disabled={busy} data-testid="profile-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save changes</>}
        </button>
      </div>
    </AppShell>
  );
}
